import { z } from 'zod';

import {
  MAX_SIGNALING_MESSAGE_BYTES,
  PROTOCOL_VERSION,
  SUPPORTED_PROTOCOL_VERSIONS,
} from './constants.js';
import { ERROR_MESSAGES, type ErrorCode } from './errors.js';
import {
  deviceNameSchema,
  errorCodeSchema,
  hostTokenSchema,
  iceCandidateSchema,
  peerIdSchema,
  peerInfoSchema,
  requestIdSchema,
  roomInfoSchema,
  sdpDescriptionSchema,
  timestampSchema,
} from './schemas.js';

/* -------------------------------------------------------------------------- */
/* Envelope                                                                   */
/* -------------------------------------------------------------------------- */

const envelopeSchema = z.object({
  type: z.string().min(1).max(64),
  payload: z.unknown(),
  requestId: requestIdSchema.optional(),
  v: z.number().int().min(1).max(1000).optional(),
});

export type MessageEnvelope = z.infer<typeof envelopeSchema>;

/* -------------------------------------------------------------------------- */
/* Client → server                                                            */
/* -------------------------------------------------------------------------- */

export const createRoomMessageSchema = z
  .object({
    type: z.literal('create_room'),
    payload: z
      .object({
        /** Proof that this client created the room over the HTTP API. */
        hostToken: hostTokenSchema,
        deviceName: deviceNameSchema,
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const joinRoomMessageSchema = z
  .object({
    type: z.literal('join_room'),
    payload: z
      .object({
        deviceName: deviceNameSchema,
        /** Present when the host reconnects after a network change. */
        hostToken: hostTokenSchema.optional(),
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const leaveRoomMessageSchema = z
  .object({
    type: z.literal('leave_room'),
    payload: z.object({}).strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

const targetedSdpSchema = z
  .object({
    targetPeerId: peerIdSchema,
    sdp: sdpDescriptionSchema,
  })
  .strict();

export const offerMessageSchema = z
  .object({
    type: z.literal('offer'),
    payload: targetedSdpSchema,
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const answerMessageSchema = z
  .object({
    type: z.literal('answer'),
    payload: targetedSdpSchema,
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const iceCandidateMessageSchema = z
  .object({
    type: z.literal('ice_candidate'),
    payload: z
      .object({
        targetPeerId: peerIdSchema,
        candidate: iceCandidateSchema.nullable(),
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const heartbeatMessageSchema = z
  .object({
    type: z.literal('heartbeat'),
    payload: z.object({}).strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const removePeerMessageSchema = z
  .object({
    type: z.literal('remove_peer'),
    payload: z.object({ peerId: peerIdSchema }).strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const clientMessageSchema = z.discriminatedUnion('type', [
  createRoomMessageSchema,
  joinRoomMessageSchema,
  leaveRoomMessageSchema,
  offerMessageSchema,
  answerMessageSchema,
  iceCandidateMessageSchema,
  heartbeatMessageSchema,
  removePeerMessageSchema,
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type ClientMessageType = ClientMessage['type'];
export type ClientMessageOf<K extends ClientMessageType> = Extract<ClientMessage, { type: K }>;
export type ClientMessagePayload<K extends ClientMessageType> = ClientMessageOf<K>['payload'];

/* -------------------------------------------------------------------------- */
/* Server → client                                                            */
/* -------------------------------------------------------------------------- */

export const roomCreatedMessageSchema = z
  .object({
    type: z.literal('room_created'),
    payload: z
      .object({
        room: roomInfoSchema,
        self: peerInfoSchema,
        peers: z.array(peerInfoSchema).max(64),
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const roomJoinedMessageSchema = z
  .object({
    type: z.literal('room_joined'),
    payload: z
      .object({
        room: roomInfoSchema,
        self: peerInfoSchema,
        peers: z.array(peerInfoSchema).max(64),
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const peerJoinedMessageSchema = z
  .object({
    type: z.literal('peer_joined'),
    payload: z.object({ peer: peerInfoSchema }).strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const peerLeftMessageSchema = z
  .object({
    type: z.literal('peer_left'),
    payload: z
      .object({
        peerId: peerIdSchema,
        reason: z.enum(['left', 'disconnected', 'removed', 'room_closed', 'replaced']),
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

const forwardedSdpSchema = z
  .object({
    fromPeerId: peerIdSchema,
    sdp: sdpDescriptionSchema,
  })
  .strict();

export const serverOfferMessageSchema = z
  .object({
    type: z.literal('offer'),
    payload: forwardedSdpSchema,
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const serverAnswerMessageSchema = z
  .object({
    type: z.literal('answer'),
    payload: forwardedSdpSchema,
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const serverIceCandidateMessageSchema = z
  .object({
    type: z.literal('ice_candidate'),
    payload: z
      .object({
        fromPeerId: peerIdSchema,
        candidate: iceCandidateSchema.nullable(),
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const roomErrorMessageSchema = z
  .object({
    type: z.literal('room_error'),
    payload: z
      .object({
        code: errorCodeSchema,
        message: z.string().min(1).max(300),
        /** When true the server will close the socket after this message. */
        fatal: z.boolean(),
        issues: z
          .array(
            z.object({
              path: z.string().max(200),
              message: z.string().max(300),
            }),
          )
          .max(8)
          .optional(),
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const heartbeatAckMessageSchema = z
  .object({
    type: z.literal('heartbeat_ack'),
    payload: z
      .object({
        serverTime: timestampSchema,
        peerCount: z.number().int().nonnegative(),
      })
      .strict(),
    requestId: requestIdSchema.optional(),
    v: z.number().int().optional(),
  })
  .strict();

export const serverMessageSchema = z.discriminatedUnion('type', [
  roomCreatedMessageSchema,
  roomJoinedMessageSchema,
  peerJoinedMessageSchema,
  peerLeftMessageSchema,
  serverOfferMessageSchema,
  serverAnswerMessageSchema,
  serverIceCandidateMessageSchema,
  roomErrorMessageSchema,
  heartbeatAckMessageSchema,
]);

export type ServerMessage = z.infer<typeof serverMessageSchema>;
export type ServerMessageType = ServerMessage['type'];
export type ServerMessageOf<K extends ServerMessageType> = Extract<ServerMessage, { type: K }>;
export type ServerMessagePayload<K extends ServerMessageType> = ServerMessageOf<K>['payload'];

/* -------------------------------------------------------------------------- */
/* Validation                                                                 */
/* -------------------------------------------------------------------------- */

export interface ProtocolIssue {
  path: string;
  message: string;
}

export interface ProtocolErrorPayload {
  code: ErrorCode;
  message: string;
  issues?: ProtocolIssue[];
}

export type ValidationResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: ProtocolErrorPayload };

export const CLIENT_MESSAGE_TYPES = [
  'create_room',
  'join_room',
  'leave_room',
  'offer',
  'answer',
  'ice_candidate',
  'heartbeat',
  'remove_peer',
] as const satisfies readonly ClientMessageType[];

export const SERVER_MESSAGE_TYPES = [
  'room_created',
  'room_joined',
  'peer_joined',
  'peer_left',
  'offer',
  'answer',
  'ice_candidate',
  'room_error',
  'heartbeat_ack',
] as const satisfies readonly ServerMessageType[];

const KNOWN_CLIENT_TYPES = new Set<string>(CLIENT_MESSAGE_TYPES);
const KNOWN_SERVER_TYPES = new Set<string>(SERVER_MESSAGE_TYPES);

function invalid(code: ErrorCode, message: string, issues?: ProtocolIssue[]): ValidationResult<never> {
  return { ok: false, error: { code, message, ...(issues ? { issues } : {}) } };
}

function toIssues(error: z.ZodError): ProtocolIssue[] {
  return error.issues.slice(0, 8).map((issue) => ({
    path: issue.path.join('.') || '(root)',
    message: issue.message.slice(0, 300),
  }));
}

function assertEnvelope(
  input: unknown,
  knownTypes: Set<string>,
): ValidationResult<MessageEnvelope> {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return invalid('BAD_REQUEST', 'Messages must be JSON objects.');
  }

  const candidate = input as Record<string, unknown>;
  const type = candidate['type'];

  if (typeof type !== 'string' || !knownTypes.has(type)) {
    return invalid('UNKNOWN_MESSAGE', 'Unknown message type.');
  }

  const version = candidate['v'];
  if (version !== undefined) {
    if (
      typeof version !== 'number' ||
      !Number.isInteger(version) ||
      !SUPPORTED_PROTOCOL_VERSIONS.includes(version)
    ) {
      return invalid(
        'UNSUPPORTED_VERSION',
        `This server speaks protocol version ${PROTOCOL_VERSION}.`,
      );
    }
  }

  const envelope = envelopeSchema.safeParse(input);
  if (!envelope.success) {
    return invalid('BAD_REQUEST', 'Malformed message envelope.', toIssues(envelope.error));
  }

  return { ok: true, value: envelope.data };
}

export function validateClientMessage(input: unknown): ValidationResult<ClientMessage> {
  const envelope = assertEnvelope(input, KNOWN_CLIENT_TYPES);
  if (!envelope.ok) return envelope;

  const parsed = clientMessageSchema.safeParse(input);
  if (!parsed.success) {
    return invalid('VALIDATION_FAILED', ERROR_MESSAGES.VALIDATION_FAILED, toIssues(parsed.error));
  }

  return { ok: true, value: { ...parsed.data, v: PROTOCOL_VERSION } };
}

export function validateServerMessage(input: unknown): ValidationResult<ServerMessage> {
  const envelope = assertEnvelope(input, KNOWN_SERVER_TYPES);
  if (!envelope.ok) return envelope;

  const parsed = serverMessageSchema.safeParse(input);
  if (!parsed.success) {
    return invalid('VALIDATION_FAILED', ERROR_MESSAGES.VALIDATION_FAILED, toIssues(parsed.error));
  }

  return { ok: true, value: { ...parsed.data, v: PROTOCOL_VERSION } };
}

const encoder = new TextEncoder();

export function utf8ByteLength(value: string): number {
  return encoder.encode(value).length;
}

/** Hard cap check applied before JSON parsing to avoid unnecessary work. */
export function isOversizedMessage(rawLength: number): boolean {
  return rawLength > MAX_SIGNALING_MESSAGE_BYTES;
}

export function safeJsonParse(raw: string): ValidationResult<unknown> {
  try {
    return { ok: true, value: JSON.parse(raw) as unknown };
  } catch {
    return invalid('BAD_REQUEST', 'Message is not valid JSON.');
  }
}

/* -------------------------------------------------------------------------- */
/* Builders                                                                   */
/* -------------------------------------------------------------------------- */

function withMeta<T extends object>(message: T, requestId?: string): T & { v: number } {
  return {
    ...message,
    v: PROTOCOL_VERSION,
    ...(requestId ? { requestId } : {}),
  };
}

export function createClientMessage<K extends ClientMessageType>(
  type: K,
  payload: ClientMessagePayload<K>,
  requestId?: string,
): ClientMessageOf<K> {
  return withMeta({ type, payload }, requestId) as unknown as ClientMessageOf<K>;
}

export function createServerMessage<K extends ServerMessageType>(
  type: K,
  payload: ServerMessagePayload<K>,
  requestId?: string,
): ServerMessageOf<K> {
  return withMeta({ type, payload }, requestId) as unknown as ServerMessageOf<K>;
}

export function serializeMessage(message: ClientMessage | ServerMessage): string {
  return JSON.stringify(message);
}
